import { type GetComponentServerProps } from "@sitecore-content-sdk/nextjs";
import {
  formatCoveoDate,
  formatCoveoDateWithoutConversion,
} from "../../../utils/contentHubComponentsUtils";
import { CoveoFieldName, toCoveoUILanguage } from "../../../utils/coveoUtils";
import { CoveoSearchRequest } from "lib/search/CoveoSearchApi";
import { type CardCarouselFields, type CoveoCard } from "./card-carousel.props";
import { type CoveoConfigOptions, type CoveoSearchApiQuery } from "types/Coveo";
import fallbackImage from "src/assets/images/fallback-image.jpg";

export {
  Default,
  EventSlider,
  MediaCenterBillboard,
} from "./CardCarousel.client";

// Mark as client so AppPlaceholder routes through ClientComponentWrapper
// (which uses the client componentMap). Required because the actual variants
// (Default, EventSlider) live in CardCarousel.client.tsx as 'use client'
// components — passing the full componentMap to them would serialize server
// function refs (BYOCServerWrapper) across the RSC boundary.
export const componentType = "client";

const TAXONOMY_LABEL = "TaxonomyLabel";
const TAXONOMY_VALUE = "Value";
const DEFAULT_NUMBER_OF_CARDS = 8;

type TaxonomyLike = {
  fields?: {
    [TAXONOMY_LABEL]?: { value?: string };
    [TAXONOMY_VALUE]?: { value?: string };
  };
};

const collectValues = (
  list: TaxonomyLike[] | undefined,
  fieldName: typeof TAXONOMY_LABEL | typeof TAXONOMY_VALUE,
): string =>
  (list ?? [])
    .map((item) => `"${item?.fields?.[fieldName]?.value ?? ""}"`)
    .filter((v) => v !== '""')
    .join(", ");

const getSortCriteria = (
  isMediaCenterBillboard: boolean,
  sortField?: string,
  sortDirection?: string,
): string | undefined => {
  if (isMediaCenterBillboard) {
    return "@page_publication_date descending";
  }

  if (!sortField || !sortDirection) {
    return undefined;
  }

  return `@${sortField} ${sortDirection.toLowerCase()}`;
};

const addTemplateFields = (
  selectedTemplates: string,
  fieldsToInclude: string[],
): void => {
  if (selectedTemplates.includes("NewsPage")) {
    fieldsToInclude.push("taxonomy_news_types", "taxonomy_news_topics");
  }

  if (selectedTemplates.includes("ArticlePage")) {
    fieldsToInclude.push(
      "article_author",
      "taxonomy_article_topics",
      "taxonomy_article_types",
    );
  }

  if (selectedTemplates.includes("EventPage")) {
    fieldsToInclude.push(
      "event_title",
      "event_subtitle",
      "event_description",
      "event_image",
      "event_location",
      "event_start",
      "event_end",
      "event_timezone",
      "event_type",
      "taxonomy_news_topics",
      "taxonomy_news_types",
    );
  }

  if (selectedTemplates.includes("StoryPage")) {
    fieldsToInclude.push("taxonomy_story_types", "taxonomy_story_topics");
  }

  if (selectedTemplates.includes("LocationPage")) {
    fieldsToInclude.push(
      "taxonomy_location_types",
      "taxonomy_location_regions",
    );
  }

  if (selectedTemplates.includes("SolutionPage")) {
    fieldsToInclude.push("taxonomy_solutions");
  }

  if (selectedTemplates.includes("Offering")) {
    fieldsToInclude.push("taxonomy_offering_type");
  }
};

const buildContentFilters = (
  fields: CardCarouselFields,
  fieldsToInclude: string[],
  locale: string | undefined,
  itemId: string | undefined,
): string | undefined => {
  const {
    Applications,
    Solutions,
    Industries,
    Markets,
    TemplateTypes,
    BlogArticleType,
    BlogArticleTopic,
    BlogArticleAuthor,
    NewsLetterName,
    NewsType,
    NewsTopic,
    StoryType,
    StoryTopic,
    EventsType,
    LocationType,
    LocationRegion,
    Companies,
    OfferingType,
    TranslationOwner,
    GoogleAnalyticsRepGroup,
    Countries,
    Regions,
  } = (fields as unknown as CoveoConfigOptions) || {};

  const queryParts: string[] = [];

  const selectedTemplates = collectValues(TemplateTypes, TAXONOMY_VALUE);
  if (selectedTemplates) {
    queryParts.push(
      `${CoveoFieldName.PageTemplateName}==(${selectedTemplates})`,
    );

    addTemplateFields(selectedTemplates, fieldsToInclude);
  }

  const taxonomyFilters: Array<[string, string | undefined]> = [
    [CoveoFieldName.TaxonomyMarkets, collectValues(Markets, TAXONOMY_LABEL)],
    [
      CoveoFieldName.TaxonomyApplications,
      collectValues(Applications, TAXONOMY_LABEL),
    ],
    [
      CoveoFieldName.TaxonomySolutions,
      collectValues(Solutions, TAXONOMY_LABEL),
    ],
    [
      CoveoFieldName.TaxonomyIndustries,
      collectValues(Industries, TAXONOMY_LABEL),
    ],
    [
      CoveoFieldName.TaxonomyArticleTypes,
      collectValues(BlogArticleType, TAXONOMY_LABEL),
    ],
    [
      CoveoFieldName.TaxonomyArticleTopics,
      collectValues(BlogArticleTopic, TAXONOMY_LABEL),
    ],
    [
      CoveoFieldName.ArticleAuthor,
      collectValues(BlogArticleAuthor, TAXONOMY_LABEL),
    ],
    [
      CoveoFieldName.TaxonomyNewsLetterNames,
      collectValues(NewsLetterName, TAXONOMY_LABEL),
    ],
    [CoveoFieldName.TaxonomyNewsTypes, collectValues(NewsType, TAXONOMY_LABEL)],
    [
      CoveoFieldName.TaxonomyNewsTopics,
      collectValues(NewsTopic, TAXONOMY_LABEL),
    ],
    [
      CoveoFieldName.TaxonomyStoryTypes,
      collectValues(StoryType, TAXONOMY_LABEL),
    ],
    [
      CoveoFieldName.TaxonomyStoryTopics,
      collectValues(StoryTopic, TAXONOMY_LABEL),
    ],
    [CoveoFieldName.EventType, collectValues(EventsType, TAXONOMY_VALUE)],
    [
      CoveoFieldName.TaxonomyLocationTypes,
      collectValues(LocationType, TAXONOMY_LABEL),
    ],
    [
      CoveoFieldName.TaxonomyLocationRegions,
      collectValues(LocationRegion, TAXONOMY_LABEL),
    ],
    [
      CoveoFieldName.TaxonomyCompanies,
      collectValues(Companies, TAXONOMY_LABEL),
    ],
    [
      CoveoFieldName.TaxonomyOfferingType,
      collectValues(OfferingType, TAXONOMY_LABEL),
    ],
    [
      CoveoFieldName.TaxonomyTranslationOwner,
      collectValues(TranslationOwner, TAXONOMY_LABEL),
    ],
    [
      CoveoFieldName.TaxonomyCountries,
      collectValues(Countries, TAXONOMY_LABEL),
    ],
    [CoveoFieldName.TaxonomyRegions, collectValues(Regions, TAXONOMY_LABEL)],
    [
      CoveoFieldName.TaxonomyGoogleAnalyticsRepGroup,
      collectValues(GoogleAnalyticsRepGroup, TAXONOMY_LABEL),
    ],
  ];

  for (const [coveoField, value] of taxonomyFilters) {
    if (value) queryParts.push(`${coveoField}==(${value})`);
  }

  if (locale) {
    queryParts.push(`(${CoveoFieldName.PageLang}=="${locale}")`);
  }

  const formattedItemId = itemId?.replaceAll("-", "")?.toUpperCase();
  if (formattedItemId) {
    queryParts.push(`(NOT ${CoveoFieldName.PageId}=="${formattedItemId}")`);
  }

  return queryParts.length > 0 ? queryParts.join(" AND ") : undefined;
};

export const getComponentServerProps: GetComponentServerProps = async (
  rendering,
  _layoutData,
  context,
) => {
  const fields = (rendering.fields ?? {}) as unknown as CardCarouselFields;
  const params = rendering.params ?? {};

  // const segments = (context?.params?.path as string[]) || [];
  // const joined = segments.join("/");

  // Real request: last segment is the article slug
  // const slug = segments.at(-1);

  const locale = toCoveoUILanguage(
    context?.locale || _layoutData?.sitecore?.context?.language || "en-US",
  );

  const isDateOnly = params?.HideTimeInDateField === "1";
  const selectedVariant = params?.FieldNames;
  const isMediaCenterBillboard = selectedVariant === "MediaCenterBillboard";

  const fieldsToInclude = [
    "page_og_title",
    "page_og_description",
    "page_og_image",
    "page_publication_date",
    "page_publication_day",
    "page_publication_month",
    "page_publication_year",
    "page_template_name",
    "page_id",
  ];

  let aq: string | undefined;
  let cq: string | undefined;
  let dq: string | undefined;
  let lq: string | undefined;

  if (process.env.NEXT_PUBLIC_COVEO_FILTERS_ENABLED) {
    if (isMediaCenterBillboard) {
      cq = [
        `${CoveoFieldName.TaxonomyNewsTypes}==("News Item")`,
        `${CoveoFieldName.TaxonomyNewsTopics}==("Billboard")`,
      ].join(" AND ");
      aq = undefined;
      dq = undefined;
      lq = undefined;
    } else {
      cq = buildContentFilters(
        fields,
        fieldsToInclude,
        locale,
        _layoutData?.sitecore?.route?.itemId,
      );
    }
  } else {
    aq = fields?.aq?.value;
    cq = fields?.cq?.value;
    dq = fields?.dq?.value;
    lq = fields?.lq?.value;
  }

  const numberOfResultsRequested =
    (fields?.NumberOfResults?.value || DEFAULT_NUMBER_OF_CARDS) + 1;
  const sortField = fields?.SortField?.value;
  const sortDirection = fields?.SoftFieldDirection?.value;
  const sortCriteria = getSortCriteria(
    isMediaCenterBillboard,
    sortField,
    sortDirection,
  );

  const query: CoveoSearchApiQuery = {
    locale: locale || "en-us",
    searchHub: process.env.NEXT_PUBLIC_COVEO_SEARCH_HUB,
    numberOfResults: isMediaCenterBillboard ? 1 : numberOfResultsRequested,
    sortCriteria,
    pipeline: process.env.NEXT_PUBLIC_COVEO_PIPELINE,
    aq,
    cq,
    dq,
    lq,
    fieldsToInclude,
  };

  const response = await CoveoSearchRequest(query);

  const cards: CoveoCard[] = response.results.map((result) => {
    const title =
      result.raw["event_title"] || result.raw["page_og_title"] || "";
    let description = "";
    if (isMediaCenterBillboard) {
      // For billboard, use page description if og description is not available, as per design requirement
      description =
        result.raw["page_og_description"] ||
        result.raw["page_description"] ||
        result.excerpt;
    } else {
      description =
        result.raw["event_description"] ||
        result.raw["page_og_description"] ||
        result.raw["page_description"] ||
        result.excerpt;
    }
    const image =
      result.raw["event_image"] ||
      result.raw["page_og_image"] ||
      fallbackImage.src;

    let date: string | null = null;
    const templateName = result.raw["page_template_name"];

    if (templateName === "EventPage") {
      const dateRaw =
        result.raw["event_start"] || result.raw["page_publication_date"];
      date = formatCoveoDateWithoutConversion(dateRaw, isDateOnly) || null;
    } else if (
      isMediaCenterBillboard ||
      templateName === "NewsPage" ||
      templateName === "StoryPage" ||
      templateName === "ArticlePage"
    ) {
      const dateRaw = result.raw["page_publication_date"];
      date = formatCoveoDate(dateRaw, { locale: "en-US" }) || null;
    }

    return {
      title,
      desc: description,
      image,
      link: result.clickUri ?? "",
      linkLabel: fields?.ShowMoreText?.value || "",
      date,
      pageId: result.raw["page_id"] || null,
      subtitle: result.raw["event_subtitle"] || null,
      timezone: result.raw["event_timezone"] || null,
    };
  });

  return { cards, numberOfResultsRequested };
};
